import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Menu, MenuHandler, MenuItem, MenuList } from '@material-tailwind/react';
import { Bell, LogOut, User as UserIcon } from 'lucide-react';
import moment from 'moment';
import { URLS } from '../router-x/routes';
import { useAuth } from '../services/contexts/authContext';
import { useGetUserNotifications } from '../custom-hooks/use-notifications';
import { STATIC_USER_ROLES } from '../utils/enums';

// Home-only header: greeting + date on the left, notification bell + account menu on the right.
// Matches the design where this header appears only on the dashboard, not on every page.
const PortalHomeHeader = ({ name }) => {
	const navigate = useNavigate();
	const { userDetails, user, logout } = useAuth();
	const { data: notificationsData } = useGetUserNotifications();

	const unread = (notificationsData?.notifications || []).filter((n) => !n.readAt).length;
	const isTeamMember = userDetails?.role === STATIC_USER_ROLES.TEAM_MEMBER;
	const notificationsUrl = isTeamMember ? URLS.TEAM_MEMBER.NOTIFICATIONS : URLS.CUSTOMER.NOTIFICATIONS;
	const profileUrl = isTeamMember ? URLS.TEAM_MEMBER.PROFILE : URLS.CUSTOMER.PROFILE;

	const displayName = name || user?.displayName || 'User';
	const initial = (displayName || 'U').trim().charAt(0).toUpperCase();

	const handleLogout = async () => {
		try {
			const result = await logout();
			if (result?.wasImpersonating) {
				navigate(result.returnPath || URLS.ADMIN.ROOT);
			}
		} catch (err) {
			console.error('Logout failed:', err);
		}
	};

	return (
		<div className="mb-6 flex items-start justify-between gap-4">
			<div>
				<h1 className="font-display text-[25px] font-bold text-ink">Welcome back, {displayName}</h1>
				<p className="mt-1 text-sm text-[#8C8175]">{moment().format('dddd, MMMM D, YYYY')}</p>
			</div>
			<div className="flex shrink-0 items-center gap-3.5">
				<button
					onClick={() => navigate(notificationsUrl)}
					aria-label="Notifications"
					className="relative flex h-[42px] w-[42px] items-center justify-center rounded-xl border border-[#ECE3D5] bg-white text-[#6B675F] transition-colors hover:bg-cream-input"
				>
					<Bell size={20} />
					{unread > 0 && (
						<span className="absolute -right-1.5 -top-1.5 rounded-full bg-brand px-1.5 py-px text-[10px] font-bold text-white">
							{unread}
						</span>
					)}
				</button>

				<Menu placement="bottom-end">
					<MenuHandler>
						<button
							aria-label="Account menu"
							className="flex h-[42px] w-[42px] items-center justify-center rounded-xl bg-brand font-display text-base font-semibold text-white outline-none transition-transform hover:scale-105"
						>
							{initial}
						</button>
					</MenuHandler>
					<MenuList className="min-w-[180px]">
						<div className="px-3 py-2">
							<div className="truncate text-sm font-semibold text-ink">{displayName}</div>
							{user?.email && <div className="truncate text-xs text-[#8C8175]">{user.email}</div>}
						</div>
						<hr className="my-1 border-[#ECE3D5]" />
						<MenuItem onClick={() => navigate(profileUrl)} className="flex items-center gap-2 text-sm text-ink">
							<UserIcon size={16} /> Profile
						</MenuItem>
						<MenuItem onClick={handleLogout} className="flex items-center gap-2 text-sm text-[#BE3B28]">
							<LogOut size={16} /> Logout
						</MenuItem>
					</MenuList>
				</Menu>
			</div>
		</div>
	);
};

export default PortalHomeHeader;
